import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Avatar, Alert, Divider, Tooltip,
} from '@mui/material';
import { Add, TrendingUp, Payments, Percent, History, AccountBalance } from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { ACCENTS, tint, colorForName } from '../theme/accents';
import { formatINR } from '../utils/currency';
import ResponsiveDataGrid from '../components/ResponsiveDataGrid';
import EmptyState from '../components/EmptyState';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { StatusIndicator, MethodOptionContent, METHOD_VALUES } from '../components/Indicators';

const TXN_LABEL = {
  disbursement: 'Borrowed',
  interest_accrual: 'Interest charged',
  repayment: 'Repayment',
  adjustment: 'Adjustment',
  writeoff: 'Written off',
};

const TXN_ACCENT = {
  disbursement: ACCENTS.indigo,
  interest_accrual: ACCENTS.marigold,
  repayment: ACCENTS.emerald,
  adjustment: ACCENTS.silver,
  writeoff: ACCENTS.vermilion,
};

// DataGrid renders this instead of a bare "No rows" line.
const NoRows = () => (
  <EmptyState
    icon={<AccountBalance />}
    accent={ACCENTS.magenta}
    title="Nobody has borrowed yet"
    description="Loans from the fund appear here with what is still owed and the interest accrued against them."
  />
);

const Loans = () => {
  const { isAdmin } = useAuth();
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rates, setRates] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [newLoanOpen, setNewLoanOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [repayOpen, setRepayOpen] = useState(null);

  const [loanForm, setLoanForm] = useState({
    borrowerUserId: '', principalAmount: '', disbursedDate: dayjs().format('YYYY-MM-DD'),
    interestRateOverride: '', purpose: '',
  });
  const [rateForm, setRateForm] = useState({ ratePercent: '', effectiveFrom: dayjs().format('YYYY-MM-DD'), notes: '' });
  const [repayForm, setRepayForm] = useState({ amount: '', date: dayjs().format('YYYY-MM-DD'), paymentMethod: 'Cash', notes: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [loansRes, summaryRes, ratesRes, usersRes] = await Promise.all([
        axios.get('/api/loans'),
        axios.get('/api/loans/summary'),
        axios.get('/api/loans/rates'),
        axios.get('/api/users'),
      ]);
      setLoans(loansRes.data);
      setSummary(summaryRes.data);
      setRates(ratesRes.data);
      setMembers(usersRes.data.filter(u => !u.is_system_admin));
    } catch (error) {
      toast.error('Failed to load loans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const columns = [
    {
      field: 'borrower_name',
      headerName: 'Borrower',
      flex: 1.5,
      minWidth: 155,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 0 }}>
          <Avatar sx={{
            width: 30, height: 30, fontSize: 13, fontWeight: 700, flexShrink: 0,
            bgcolor: tint(colorForName(params.value || ''), 0.2),
            color: colorForName(params.value || ''),
            border: '1px solid',
            borderColor: tint(colorForName(params.value || ''), 0.5),
          }}>
            {(params.value || '?').charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {params.value}
            </Typography>
            {params.row.purpose && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {params.row.purpose}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'principal_amount', headerName: 'Borrowed', width: 105,
      align: 'right', headerAlign: 'right',
      renderCell: (params) => <span className="tabular">{formatINR(params.value)}</span>,
    },
    {
      field: 'principalOutstanding', headerName: 'Principal left', width: 120,
      align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" className="tabular" sx={{ fontWeight: 700 }}>
          {formatINR(params.value)}
        </Typography>
      ),
    },
    {
      field: 'interestOutstanding', headerName: 'Interest due', width: 115,
      align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" className="tabular"
          sx={{ color: params.value > 0 ? ACCENTS.marigold : 'text.secondary' }}>
          {formatINR(params.value)}
        </Typography>
      ),
    },
    {
      field: 'disbursed_date', headerName: 'Since', width: 105,
      renderCell: (params) => dayjs(params.value).format('D MMM YYYY'),
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (params) => <StatusIndicator status={params.value} domain="loans" size="small" />,
    },
    ...(isAdmin ? [{
      field: 'actions', headerName: '', width: 84, sortable: false, filterable: false,
      align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        params.row.status === 'active' ? (
          <Button size="small" onClick={(e) => { e.stopPropagation(); setRepayOpen(params.row); }}>
            Repay
          </Button>
        ) : null
      ),
    }] : []),
  ];

  const openDetail = async (loanId) => {
    try {
      const res = await axios.get(`/api/loans/${loanId}`);
      setDetail(res.data);
    } catch (error) {
      toast.error('Could not open the loan');
    }
  };

  const handleCreateLoan = async () => {
    try {
      await axios.post('/api/loans', {
        borrowerUserId: Number(loanForm.borrowerUserId),
        principalAmount: parseFloat(loanForm.principalAmount),
        disbursedDate: loanForm.disbursedDate,
        interestRateOverride: loanForm.interestRateOverride === '' ? null : parseFloat(loanForm.interestRateOverride),
        purpose: loanForm.purpose || null,
      });
      toast.success('Loan recorded');
      setNewLoanOpen(false);
      setLoanForm({ borrowerUserId: '', principalAmount: '', disbursedDate: dayjs().format('YYYY-MM-DD'), interestRateOverride: '', purpose: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.errors?.[0]?.msg || error.response?.data?.error || 'Failed to record loan');
    }
  };

  const handleSetRate = async () => {
    try {
      const res = await axios.post('/api/loans/rates', {
        ratePercent: parseFloat(rateForm.ratePercent),
        effectiveFrom: rateForm.effectiveFrom,
        notes: rateForm.notes || null,
      });
      toast.success(res.data.message);
      setRateOpen(false);
      setRateForm({ ratePercent: '', effectiveFrom: dayjs().format('YYYY-MM-DD'), notes: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.errors?.[0]?.msg || 'Failed to set rate');
    }
  };

  const handleRepay = async () => {
    try {
      const res = await axios.post(`/api/loans/${repayOpen.id}/repayment`, {
        amount: parseFloat(repayForm.amount),
        date: repayForm.date,
        paymentMethod: repayForm.paymentMethod,
        notes: repayForm.notes || null,
      });
      toast.success(
        `${formatINR(res.interestPaid ?? res.data.interestPaid)} interest, ${formatINR(res.data.principalPaid)} principal` +
        (res.data.closed ? ' — loan closed' : '')
      );
      setRepayOpen(null);
      setRepayForm({ amount: '', date: dayjs().format('YYYY-MM-DD'), paymentMethod: 'Cash', notes: '' });
      fetchAll();
      if (detail) openDetail(detail.id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to record repayment');
    }
  };

  const handleAccrue = async () => {
    try {
      const res = await axios.post('/api/loans/accrue', { month: dayjs().format('YYYY-MM') });
      toast.success(`Interest charged on ${res.data.charged} loan${res.data.charged === 1 ? '' : 's'}`);
      fetchAll();
    } catch (error) {
      toast.error('Failed to charge interest');
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Lending"
        title="Loans"
        subtitle={summary ? `Interest ${summary.currentRate}% per month on the outstanding balance` : undefined}
        accent={ACCENTS.magenta}
        actions={isAdmin && (
          <>
            <Button variant="outlined" startIcon={<Percent />} onClick={() => setRateOpen(true)}>Rate</Button>
            <Button variant="outlined" startIcon={<TrendingUp />} onClick={handleAccrue}>Charge interest</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setNewLoanOpen(true)}>New loan</Button>
          </>
        )}
      />

      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard title="Lent out" value={formatINR(summary?.principalOutstanding)} subtitle={`${summary?.activeLoans || 0} active`} icon={<Payments />} accent={ACCENTS.indigo} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Interest due" value={formatINR(summary?.interestOutstanding)} subtitle="Unpaid" icon={<Percent />} accent={ACCENTS.marigold} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Interest earned" value={formatINR(summary?.interestEarned)} subtitle="Received" icon={<TrendingUp />} accent={ACCENTS.emerald} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Current rate" value={`${summary?.currentRate ?? 0}%`} subtitle="Per month" icon={<History />} accent={ACCENTS.magenta} />
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', border: '1px solid', borderColor: 'divider' }}>
        <ResponsiveDataGrid
          mobile={{ title: 'borrower_name', amount: 'principal_amount' }}
          rows={loans}
          columns={columns}
          getRowId={(row) => row.id}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row.id)}
          autoHeight
          loading={loading}
          slots={{ noRowsOverlay: NoRows }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // v6 collapses the no-rows overlay under autoHeight, clipping it
            '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            '& .MuiDataGrid-overlayWrapperInner': { minHeight: 300 },
            '& .MuiDataGrid-cell:hover': { color: 'primary.main' },
          }}
        />
      </Paper>

      {/* Rate history - shows that past months keep their own rate */}
      {rates?.rates?.length > 0 && (
        <Paper sx={{ p: 2.5, mt: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" display="block" sx={{ mb: 1.5, color: ACCENTS.magenta }}>
            Rate history
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {rates.rates.map(r => (
              <Tooltip key={r.id} title={r.notes || ''}>
                <Chip
                  size="small"
                  label={`${Number(r.rate_percent)}% from ${dayjs(r.effective_from).format('D MMM YYYY')}`}
                  sx={{ bgcolor: tint(ACCENTS.magenta, 0.14), color: ACCENTS.magenta }}
                />
              </Tooltip>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Changing the rate applies to future months only. Interest already charged keeps the rate it was charged at.
          </Typography>
        </Paper>
      )}

      {/* New loan */}
      <Dialog open={newLoanOpen} onClose={() => setNewLoanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record a loan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Borrower" value={loanForm.borrowerUserId}
                onChange={(e) => setLoanForm({ ...loanForm, borrowerUserId: e.target.value })}>
                {members.map(m => <MenuItem key={m.id} value={m.id}>{m.full_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Amount borrowed" value={loanForm.principalAmount}
                onChange={(e) => setLoanForm({ ...loanForm, principalAmount: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }}
                value={loanForm.disbursedDate}
                onChange={(e) => setLoanForm({ ...loanForm, disbursedDate: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Purpose" value={loanForm.purpose}
                onChange={(e) => setLoanForm({ ...loanForm, purpose: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Fixed rate for this loan (optional)"
                value={loanForm.interestRateOverride}
                onChange={(e) => setLoanForm({ ...loanForm, interestRateOverride: e.target.value })}
                helperText={`Leave blank to follow the group rate, currently ${summary?.currentRate ?? 0}% per month. A fixed rate here ignores future group changes.`} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewLoanOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateLoan}>Record loan</Button>
        </DialogActions>
      </Dialog>

      {/* Repayment */}
      <Dialog open={Boolean(repayOpen)} onClose={() => setRepayOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Repayment — {repayOpen?.borrower_name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Owes {formatINR(repayOpen?.interestOutstanding)} interest and {formatINR(repayOpen?.principalOutstanding)} principal.
            Interest is cleared first; anything above it reduces the principal.
          </Alert>
          <TextField fullWidth type="number" label="Amount paid" sx={{ mb: 2 }}
            value={repayForm.amount} onChange={(e) => setRepayForm({ ...repayForm, amount: e.target.value })} />
          <TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            value={repayForm.date} onChange={(e) => setRepayForm({ ...repayForm, date: e.target.value })} />
          <TextField select fullWidth label="Paid via" value={repayForm.paymentMethod}
            onChange={(e) => setRepayForm({ ...repayForm, paymentMethod: e.target.value })}>
            {METHOD_VALUES.map(m => (
              <MenuItem key={m} value={m}><MethodOptionContent method={m} /></MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepayOpen(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRepay}>Record repayment</Button>
        </DialogActions>
      </Dialog>

      {/* Rate change */}
      <Dialog open={rateOpen} onClose={() => setRateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change the interest rate</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Applies from the date you choose onwards. Interest already charged is never recalculated.
          </Alert>
          <TextField fullWidth type="number" label="Rate percent per month" sx={{ mb: 2 }}
            value={rateForm.ratePercent} onChange={(e) => setRateForm({ ...rateForm, ratePercent: e.target.value })} />
          <TextField fullWidth type="date" label="Effective from" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            value={rateForm.effectiveFrom} onChange={(e) => setRateForm({ ...rateForm, effectiveFrom: e.target.value })} />
          <TextField fullWidth label="Why (optional)" value={rateForm.notes}
            onChange={(e) => setRateForm({ ...rateForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSetRate}>Set rate</Button>
        </DialogActions>
      </Dialog>

      {/* Ledger */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.borrower_name} — {formatINR(detail?.principal_amount)} borrowed {detail && dayjs(detail.disbursed_date).format('D MMM YYYY')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Principal left</Typography>
              <Typography className="tabular" variant="h6">{formatINR(detail?.principalOutstanding)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Interest due</Typography>
              <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.marigold }}>{formatINR(detail?.interestOutstanding)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Interest paid</Typography>
              <Typography className="tabular" variant="h6" sx={{ color: ACCENTS.emerald }}>{formatINR(detail?.interestPaid)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="overline" display="block">Rate now</Typography>
              <Typography className="tabular" variant="h6">{detail?.currentRate}%</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Principal</TableCell>
                <TableCell align="right">Interest</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Balance after</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail?.transactions?.map(t => (
                <TableRow key={t.id}>
                  <TableCell>{dayjs(t.transaction_date).format('D MMM YYYY')}</TableCell>
                  <TableCell>
                    <Chip size="small" label={TXN_LABEL[t.type] || t.type}
                      sx={{ bgcolor: tint(TXN_ACCENT[t.type] || ACCENTS.silver, 0.16), color: TXN_ACCENT[t.type] || ACCENTS.silver }} />
                  </TableCell>
                  <TableCell align="right" className="tabular">{formatINR(t.amount)}</TableCell>
                  <TableCell align="right" className="tabular">{Number(t.principal_component) ? formatINR(t.principal_component) : '—'}</TableCell>
                  <TableCell align="right" className="tabular">{Number(t.interest_component) ? formatINR(t.interest_component) : '—'}</TableCell>
                  <TableCell align="right" className="tabular">{t.rate_percent_applied ? `${Number(t.rate_percent_applied)}%` : '—'}</TableCell>
                  <TableCell align="right" className="tabular">{formatINR(t.principal_balance_after)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Loans;
